import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart, formatTL } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

const FALLBACK = "https://images.unsplash.com/photo-1643822308521-1da534425d82?w=400";

export default function Cart() {
  const { items, setQty, remove, count } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const isDealer = user && user.role === "dealer";
  const dealerDiscount = isDealer ? (user.discount_percent || 0) : 0;

  const lineUnit = (item) => {
    if (isDealer && item.price > 0 && dealerDiscount > 0) {
      return +(item.price * (1 - dealerDiscount / 100)).toFixed(2);
    }
    return item.price;
  };

  const subtotal = items.reduce((sum, it) => sum + lineUnit(it) * it.quantity, 0);

  return (
    <>
      <SEO title="Sepet" description="Wünberg sipariş sepeti" />
      <section className="pt-32 pb-12 border-b border-white/10">
        <div className="wb-container">
          <div className="wb-overline">Sepet</div>
          <h1 className="wb-h1 mt-3">Sepetiniz</h1>
        </div>
      </section>

      <section className="wb-section">
        <div className="wb-container">
          {count === 0 ? (
            <div className="wb-glass p-16 text-center">
              <ShoppingBag size={32} className="mx-auto text-[#d4af37]" />
              <h2 className="font-serif text-2xl mt-5">Sepetiniz boş</h2>
              <p className="text-sm text-[#a0aabf] mt-3">Ürünleri keşfedin ve sepete eklemeye başlayın.</p>
              <Link to="/urunler" className="wb-btn-primary mt-8 inline-flex" data-testid="cart-empty-cta">
                Ürünleri Gör <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                {items.map((item) => {
                  const unit = lineUnit(item);
                  return (
                    <div key={item.product_id} className="wb-card p-5 flex flex-col sm:flex-row gap-5" data-testid={`cart-item-${item.slug}`}>
                      <div className="w-full sm:w-32 h-32 bg-[#0c1f44] flex-shrink-0 overflow-hidden">
                        <img src={item.image_url || FALLBACK} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <Link to={`/urunler/${item.slug}`} className="font-serif text-lg hover:text-[#d4af37]">{item.name}</Link>
                            <div className="text-xs text-[#a0aabf] mt-1">{item.volume}</div>
                            {isDealer && dealerDiscount > 0 && item.price > 0 && (
                              <div className="text-xs text-[#d4af37] mt-1">Bayi indirimi: %{dealerDiscount}</div>
                            )}
                          </div>
                          <button onClick={() => remove(item.product_id)} className="p-2 text-[#a0aabf] hover:text-red-400" data-testid={`cart-remove-${item.slug}`}>
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="mt-auto flex items-end justify-between pt-4">
                          <div className="flex items-center border border-white/15">
                            <button onClick={() => setQty(item.product_id, item.quantity - 1)} className="p-2 hover:bg-white/5" data-testid={`qty-dec-${item.slug}`}>
                              <Minus size={14} />
                            </button>
                            <input
                              type="number" min="1"
                              value={item.quantity}
                              onChange={(e) => setQty(item.product_id, parseInt(e.target.value || "1"))}
                              className="w-12 bg-transparent text-center text-sm outline-none"
                              data-testid={`qty-input-${item.slug}`}
                            />
                            <button onClick={() => setQty(item.product_id, item.quantity + 1)} className="p-2 hover:bg-white/5" data-testid={`qty-inc-${item.slug}`}>
                              <Plus size={14} />
                            </button>
                          </div>
                          <div className="text-right">
                            {item.price > 0 ? (
                              <>
                                {isDealer && dealerDiscount > 0 && (
                                  <div className="text-xs text-[#a0aabf] line-through">{formatTL(item.price * item.quantity)}</div>
                                )}
                                <div className="font-serif text-lg text-[#d4af37]">{formatTL(unit * item.quantity)}</div>
                              </>
                            ) : (
                              <div className="text-xs text-[#a0aabf]">Fiyat sorulacak</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <aside className="wb-card p-6 h-fit sticky top-28">
                <div className="wb-overline">Özet</div>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#a0aabf]">Ürün adedi</span>
                    <span>{count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#a0aabf]">Ara toplam</span>
                    <span>{formatTL(subtotal)}</span>
                  </div>
                  {isDealer && dealerDiscount > 0 && (
                    <div className="flex justify-between text-[#d4af37]">
                      <span>Bayi indirimi</span>
                      <span>%{dealerDiscount}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-3 flex justify-between font-serif text-xl">
                    <span>Toplam</span>
                    <span className="text-[#d4af37]">{formatTL(subtotal)}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#a0aabf]">KDV %20 dahildir</div>
                  {isDealer && (
                    <div className="text-xs text-[#a0aabf] bg-[#d4af37]/5 border border-[#d4af37]/20 p-3 mt-2">
                      Bayi siparişlerinde <strong className="text-[#d4af37]">kargo alıcıya aittir</strong>.
                    </div>
                  )}
                </div>
                <button onClick={() => nav("/siparis")} className="wb-btn-primary w-full mt-6" data-testid="cart-checkout">
                  Sipariş Formuna Geç <ArrowRight size={16} />
                </button>
                <Link to="/urunler" className="block text-center text-xs text-[#a0aabf] hover:text-[#d4af37] mt-4">
                  Alışverişe Devam Et
                </Link>
              </aside>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
